import { useEffect, useId, useRef } from "react"
import { X } from "lucide-react"
import { useI18n } from "../../i18n"
import type { TranslationKey } from "../../i18n/types"
import { useSettingsSurface } from "./useSettingsSurface"
import type { PanelProps, SettingsPanelComponent, SettingsSection } from "./types"
import { CompanionPanel } from "./panels/CompanionPanel"
import { ConnectionsPanel } from "./panels/ConnectionsPanel"
import { ModelsPanel } from "./panels/ModelsPanel"
import { SchedulesPanel } from "./panels/SchedulesPanel"

const SECTION_ORDER: readonly SettingsSection[] = ["companion", "connections", "models", "schedules"]

// Record<SettingsSection, ...> is the compile-time guarantee the ticket asks
// for: add a member to SettingsSection without adding its panel here and
// `npm run typecheck:client` fails on this line, not at runtime with a blank
// panel. See settings/__tests__/section-panels.test-d.ts for the pinned
// assertion.
export const SECTION_PANELS: Record<SettingsSection, SettingsPanelComponent> = {
  companion: CompanionPanel,
  connections: ConnectionsPanel,
  models: ModelsPanel,
  schedules: SchedulesPanel,
}

const SECTION_LABEL_KEYS: Record<SettingsSection, TranslationKey> = {
  companion: "settings.section.companion",
  connections: "settings.section.connections",
  models: "settings.section.models",
  schedules: "settings.section.schedules",
}

export interface SettingsSurfaceProps extends PanelProps {
  /** Mirrors prefs.reducedMotion — disables the open/close transition. */
  reducedMotion?: boolean
}

/**
 * Full-bleed settings surface, mounted ONCE in AppShell. Open/section state
 * lives entirely in the URL fragment ('#settings/<section>') via
 * useSettingsSurface — closing lands the page back wherever it was, and a
 * reload/deep-link straight into a section works because there is no other
 * source of truth to desync from.
 */
export function SettingsSurface(props: SettingsSurfaceProps) {
  const { reducedMotion, ...panelProps } = props
  const { isOpen, section, show, close } = useSettingsSurface()
  const { t } = useI18n()
  const surfaceRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)
  const titleId = useId()

  // Focus management: move focus into the surface on open, return it to
  // whatever was focused before opening on close.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      openerRef.current = document.activeElement as HTMLElement | null
      surfaceRef.current?.focus()
    } else if (!isOpen && wasOpenRef.current) {
      openerRef.current?.focus()
      openerRef.current = null
    }
    wasOpenRef.current = isOpen
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close()
        return
      }
      // Focus trap: the surface is a full-bleed role="dialog" aria-modal="true"
      // over a still-mounted, still-interactive AppShell — without this, Tab
      // past the last control silently releases focus into the hidden chat
      // behind it. Mirrors ModelSwitchConfirmDialog.tsx's Tab/Shift+Tab cycle.
      // `:not([tabindex="-1"])` on the button clause matters here specifically
      // because ModelsPanel's radio rows and the composer dropdown use roving
      // tabindex (only the checked row is tabIndex 0) — without excluding
      // tabindex="-1" buttons, this trap's "focusable" list would include rows
      // that aren't actually reachable by a real Tab press.
      if (e.key === "Tab" && surfaceRef.current) {
        const focusable = Array.from(
          surfaceRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]):not([tabindex="-1"]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
          )
        )
        if (focusable.length === 0) return
        e.preventDefault()
        const activeIndex = focusable.indexOf(document.activeElement as HTMLElement)
        if (e.shiftKey) {
          const prev = activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1
          focusable[prev].focus()
        } else {
          const next = activeIndex >= focusable.length - 1 ? 0 : activeIndex + 1
          focusable[next].focus()
        }
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isOpen, close])

  if (!isOpen) return null

  const Panel = SECTION_PANELS[section]
  const sectionIndex = SECTION_ORDER.indexOf(section)

  return (
    <div
      className={`settings-surface${reducedMotion ? " settings-surface--no-motion" : ""}`}
      ref={surfaceRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="settings-surface__header">
        <h1 id={titleId} className="settings-surface__title">{t("settings.title")}</h1>
        <button
          type="button"
          className="settings-surface__close"
          onClick={close}
          aria-label={t("settings.close")}
        >
          <X size={16} />
          <span className="settings-surface__esc-hint" aria-hidden="true">{t("settings.escHint")}</span>
        </button>
      </div>
      <div className="settings-surface__body">
        <nav className="settings-surface__nav" aria-label={t("settings.title")}>
          <ul className="settings-surface__section-list">
            {SECTION_ORDER.map((s, i) => (
              <li key={s}>
                <button
                  type="button"
                  className="settings-surface__section-item"
                  aria-current={s === section ? "true" : undefined}
                  onClick={() => show(s)}
                >
                  <span className="settings-surface__section-number">{String(i + 1).padStart(2, "0")}</span>
                  <span className="settings-surface__section-name">{t(SECTION_LABEL_KEYS[s])}</span>
                  {s === section && <span className="settings-surface__section-dot" aria-hidden="true" />}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="settings-surface__panel">
          <h2 className="settings-surface__panel-heading">
            {String(sectionIndex + 1).padStart(2, "0")} . {t(SECTION_LABEL_KEYS[section])}
          </h2>
          <Panel {...panelProps} />
        </div>
      </div>
    </div>
  )
}
